import React, { useState, useRef, useEffect, useCallback } from 'react';

// ─────────────────────────────────────────────────────────
// AUTH HELPERS
// ─────────────────────────────────────────────────────────
function useAuth() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const savedToken = sessionStorage.getItem('id_token');
    if (savedToken) {
      fetchUserInfo(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  // Listen for OAuth callback messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'auth_callback') {
        const { id_token, access_token } = event.data;
        sessionStorage.setItem('id_token', id_token);
        sessionStorage.setItem('access_token', access_token);
        fetchUserInfo(id_token);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const fetchUserInfo = async (idToken) => {
    try {
      const response = await fetch('/auth/me', {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(idToken);
      } else {
        sessionStorage.removeItem('id_token');
        sessionStorage.removeItem('access_token');
      }
    } catch (e) {
      console.error('Auth error:', e);
    }
    setLoading(false);
  };

  const login = () => {
    const w = 500, h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    window.open(
      '/auth/login',
      'microsoft-login',
      `width=${w},height=${h},left=${left},top=${top}`
    );
  };

  const logout = () => {
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('access_token');
    setUser(null);
    setToken(null);
  };

  return { user, token, loading, login, logout };
}

// ─────────────────────────────────────────────────────────
// API CLIENT
// ─────────────────────────────────────────────────────────
function useApi(token) {
  const sendMessage = useCallback(async (message, conversationId) => {
    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ message, conversationId }),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }, [token]);

  const uploadFile = useCallback(async (file, message, conversationId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('message', message || '');
    formData.append('conversationId', conversationId || '');

    const response = await fetch('/api/upload', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) throw new Error(`Upload Error: ${response.status}`);
    return response.json();
  }, [token]);

  return { sendMessage, uploadFile };
}

// ─────────────────────────────────────────────────────────
// LOGIN SCREEN
// ─────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f1923 0%, #1a2a3a 50%, #0d2137 100%)',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <div style={{
        width: 400, background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16,
        padding: '48px 40px', backdropFilter: 'blur(20px)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', fontSize: 24,
          }}>⚡</div>
          <h1 style={{
            color: '#fff', fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.02em',
          }}>Office Bot</h1>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, marginTop: 8 }}>
            mund-consulting.de
          </p>
        </div>

        <button onClick={onLogin} style={{
          width: '100%', padding: '14px 0',
          background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
          color: '#fff', border: 'none', borderRadius: 10,
          fontSize: 15, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          transition: 'all 0.2s',
        }}>
          <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          Mit Microsoft anmelden
        </button>

        <p style={{
          textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: 12, marginTop: 32,
        }}>
          Anmeldung über Ihr Microsoft 365 Konto
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// CHAT MESSAGE
// ─────────────────────────────────────────────────────────
function ChatMessage({ message, user }) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system_info';

  if (isSystem) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
        <div style={{
          padding: '8px 16px', background: 'rgba(45,127,249,0.08)',
          border: '1px solid rgba(45,127,249,0.12)', borderRadius: 20,
          fontSize: 13, color: 'rgba(255,255,255,0.5)',
        }}>{message.content}</div>
      </div>
    );
  }

  const initials = user?.name?.split(' ').map(n => n[0]?.toUpperCase()).join('').slice(0, 2) || 'U';

  return (
    <div style={{
      display: 'flex', gap: 12, marginBottom: 20,
      flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-start',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: isUser
          ? 'linear-gradient(135deg, #2d7ff9, #18bfff)'
          : 'linear-gradient(135deg, #1a2a3a, #243447)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: isUser ? 13 : 16, fontWeight: 600, color: '#fff', flexShrink: 0,
        border: isUser ? 'none' : '1px solid rgba(255,255,255,0.08)',
      }}>
        {isUser ? initials : '⚡'}
      </div>

      <div style={{
        maxWidth: '75%', padding: '12px 18px',
        borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
        background: isUser
          ? 'linear-gradient(135deg, #2d7ff9, #2570e0)'
          : 'rgba(255,255,255,0.04)',
        border: isUser ? 'none' : '1px solid rgba(255,255,255,0.06)',
        color: isUser ? '#fff' : 'rgba(255,255,255,0.85)',
        fontSize: 14.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {message.content}

        {message.file && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
            padding: '8px 12px', background: 'rgba(255,255,255,0.1)',
            borderRadius: 8, fontSize: 13,
          }}>
            <span>📎</span>
            <span style={{ opacity: 0.9 }}>{message.file.name}</span>
            <span style={{ opacity: 0.5, fontSize: 11 }}>
              ({(message.file.size / 1024).toFixed(0)} KB)
            </span>
          </div>
        )}

        {message.toolCall && (
          <div style={{
            marginTop: 12, padding: '10px 14px',
            background: 'rgba(45,127,249,0.08)',
            border: '1px solid rgba(45,127,249,0.15)', borderRadius: 10, fontSize: 13,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ color: '#2d7ff9' }}>⚙️</span>
              <span style={{ color: '#2d7ff9', fontWeight: 600 }}>{message.toolCall.name}</span>
            </div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
              {message.toolCall.status === 'running' && '⏳ Wird ausgeführt...'}
              {message.toolCall.status === 'done' && '✅ Abgeschlossen'}
              {message.toolCall.status === 'error' && '❌ Fehler aufgetreten'}
            </div>
          </div>
        )}

        {message.download && (
          <a href={message.download.url} download={message.download.name} style={{
            marginTop: 12, padding: '10px 14px',
            background: 'rgba(46,204,113,0.1)',
            border: '1px solid rgba(46,204,113,0.2)',
            borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8,
            cursor: 'pointer', fontSize: 13, color: '#2ecc71', fontWeight: 500,
            textDecoration: 'none',
          }}>
            <span>📥</span>{message.download.name}
          </a>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// TYPING INDICATOR
// ─────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'flex-start' }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: 'linear-gradient(135deg, #1a2a3a, #243447)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, border: '1px solid rgba(255,255,255,0.08)',
      }}>⚡</div>
      <div style={{
        padding: '14px 20px', borderRadius: '4px 16px 16px 16px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', gap: 6, alignItems: 'center',
      }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 7, height: 7, borderRadius: '50%',
            background: 'rgba(255,255,255,0.3)',
            animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
        <style>{`@keyframes pulse { 0%,80%,100% { opacity:0.3; transform:scale(0.8); } 40% { opacity:1; transform:scale(1.1); } }`}</style>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// QUICK TASKS
// ─────────────────────────────────────────────────────────
const TASKS = [
  { id: 'e_invoice', icon: '🧾', label: 'E-Rechnung', desc: 'PDF → XRechnung', prompt: 'Ich möchte eine E-Rechnung erstellen.' },
  { id: 'split_payslip', icon: '📄', label: 'Gehalt aufteilen', desc: 'Sammel-PDF → Einzel', prompt: 'Ich möchte Gehaltsabrechnungen aufteilen.' },
  { id: 'convert', icon: '🔄', label: 'PDF konvertieren', desc: 'Verschiedene Formate', prompt: 'Ich möchte ein PDF konvertieren.' },
];

// ─────────────────────────────────────────────────────────
// CHAT APP
// ─────────────────────────────────────────────────────────
function ChatApp({ user, token, onLogout }) {
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: `Guten Tag ${user.name}! 👋\n\nIch bin Ihr Office-Assistent. Ich kann Ihnen bei folgenden Aufgaben helfen:\n\n🧾  E-Rechnungen aus PDF erstellen\n📄  Gehaltsabrechnungen aufteilen\n🔄  PDFs konvertieren\n\nLaden Sie einfach ein Dokument hoch oder beschreiben Sie, was Sie brauchen.`,
  }]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [conversationId] = useState(() => crypto.randomUUID());
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const api = useApi(token);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleBotResponse = (response) => {
    // n8n AI Agent response format
    const botMessage = {
      role: 'assistant',
      content: response.output || response.message || response.text || JSON.stringify(response),
    };

    if (response.toolCall) {
      botMessage.toolCall = response.toolCall;
    }
    if (response.download) {
      botMessage.download = response.download;
    }

    setMessages(prev => [...prev, botMessage]);
    setIsTyping(false);
  };

  const handleError = (error) => {
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `Es ist ein Fehler aufgetreten: ${error.message}\n\nBitte versuchen Sie es erneut.`,
    }]);
    setIsTyping(false);
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await api.sendMessage(text, conversationId);
      handleBotResponse(response);
    } catch (error) {
      handleError(error);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files?.length) return;
    const file = files[0];

    setMessages(prev => [...prev, {
      role: 'user',
      content: input.trim() || `Datei hochgeladen: ${file.name}`,
      file: { name: file.name, size: file.size },
    }]);
    setInput('');
    setDragOver(false);
    setIsTyping(true);

    try {
      const response = await api.uploadFile(file, input, conversationId);
      handleBotResponse(response);
    } catch (error) {
      handleError(error);
    }
  };

  const handleQuickTask = (task) => {
    sendMessage(task.prompt);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const initials = user?.name?.split(' ').map(n => n[0]?.toUpperCase()).join('').slice(0, 2) || 'U';

  return (
    <div style={{
      display: 'flex', height: '100vh', background: '#0f1923',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: '#fff',
    }}>
      {/* SIDEBAR */}
      <div style={{
        width: 260, background: 'rgba(255,255,255,0.02)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', flexDirection: 'column', padding: '20px 16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32, paddingLeft: 4 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          }}>⚡</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>Office Bot</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>mund-consulting.de</div>
          </div>
        </div>

        <div style={{
          fontSize: 11, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase',
          letterSpacing: '0.08em', fontWeight: 600, marginBottom: 12, paddingLeft: 4,
        }}>Schnellaufgaben</div>

        {TASKS.map(task => (
          <button key={task.id} onClick={() => handleQuickTask(task)} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
            background: 'transparent', border: '1px solid transparent', borderRadius: 10,
            cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: 14,
            textAlign: 'left', transition: 'all 0.15s', marginBottom: 2, width: '100%',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
          >
            <span style={{ fontSize: 18 }}>{task.icon}</span>
            <div>
              <div style={{ fontWeight: 500 }}>{task.label}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>{task.desc}</div>
            </div>
          </button>
        ))}

        <div style={{ flex: 1 }} />

        {/* User */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px',
          borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 600,
          }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 500, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{user.name}</div>
            <div style={{
              fontSize: 11, color: 'rgba(255,255,255,0.3)', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{user.email}</div>
          </div>
          <button onClick={onLogout} title="Abmelden" style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
            cursor: 'pointer', fontSize: 16, padding: 4,
          }}>↪</button>
        </div>
      </div>

      {/* CHAT AREA */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{
          padding: '16px 28px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%', background: '#2ecc71',
            boxShadow: '0 0 8px rgba(46,204,113,0.4)',
          }} />
          <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
            Office Assistent — bereit
          </span>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', position: 'relative' }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); handleFileUpload(e.dataTransfer.files); }}
        >
          {dragOver && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(45,127,249,0.08)',
              border: '2px dashed rgba(45,127,249,0.4)', borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 10, fontSize: 18, color: '#2d7ff9', fontWeight: 500,
            }}>📎 Datei hier ablegen</div>
          )}

          {messages.map((msg, i) => (
            <ChatMessage key={i} message={msg} user={user} />
          ))}
          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={{ padding: '16px 28px 24px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{
            display: 'flex', gap: 10, alignItems: 'flex-end',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14,
            padding: '8px 8px 8px 18px', transition: 'border-color 0.2s',
          }}>
            <input ref={fileInputRef} type="file" accept=".pdf,.xml,.csv,.xlsx"
              style={{ display: 'none' }}
              onChange={e => handleFileUpload(e.target.files)} />
            <button onClick={() => fileInputRef.current?.click()} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
              cursor: 'pointer', fontSize: 20, padding: '4px 0', transition: 'color 0.15s',
            }}
              onMouseEnter={e => e.target.style.color = '#2d7ff9'}
              onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
              title="Datei hochladen"
            >📎</button>

            <input ref={inputRef} type="text" value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nachricht eingeben oder Datei hochladen..."
              style={{
                flex: 1, background: 'none', border: 'none', color: '#fff',
                fontSize: 15, outline: 'none', padding: '6px 0',
              }} />

            <button onClick={() => sendMessage(input)}
              disabled={!input.trim() || isTyping}
              style={{
                width: 40, height: 40, borderRadius: 10,
                background: input.trim() ? 'linear-gradient(135deg, #2d7ff9, #18bfff)' : 'rgba(255,255,255,0.06)',
                border: 'none', color: input.trim() ? '#fff' : 'rgba(255,255,255,0.2)',
                cursor: input.trim() ? 'pointer' : 'default',
                fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s', flexShrink: 0,
              }}>↑</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// LOADING SCREEN
// ─────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#0f1923', fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px', fontSize: 22,
          animation: 'pulse 2s ease-in-out infinite',
        }}>⚡</div>
        <style>{`@keyframes pulse { 0%,100% { opacity:0.5; } 50% { opacity:1; } }`}</style>
        Laden...
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────
function App() {
  const { user, token, loading, login, logout } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <LoginScreen onLogin={login} />;
  return <ChatApp user={user} token={token} onLogout={logout} />;
}

export default App;
